// @ts-nocheck
import type { PropertyDefinition } from '../../types/canonical';
import { isCreationTimeDefault } from '../../types/defaults';
import { coerceReadValue } from './readCoercion';
import type {
    ArbitraryFieldEntry,
    CategoryPropertyReadResult,
    CategoryPropertyWriteResult,
    CoercionPolicy,
} from './types';
import { DEFAULT_COERCION_POLICY } from './types';
import { normalizeWriteValue } from './writeNormalization';

/**
 * Reads a typed value for a category property from arbitraryFieldsList.
 * Applies defaultValue from the definition if entry is missing.
 * Never mutates input. Never throws.
 */
export function readPropertyValue(
  categoryId: string,
  propertyId: string,
  fields: readonly ArbitraryFieldEntry[],
  definition: PropertyDefinition,
  policy?: CoercionPolicy,
): CategoryPropertyReadResult {
  const effectivePolicy = { ...DEFAULT_COERCION_POLICY, ...policy };

  const entry = fields.find(
    f => f.categoryId === categoryId && f.propertyId === propertyId,
  );

  if (entry !== undefined) {
    const { typedValue, issues } = coerceReadValue(entry.value, definition, effectivePolicy);
    return {
      found: true,
      rawValue: entry.value,
      typedValue,
      usedDefault: false,
      issues,
    };
  }

  // Entry not found — check for defaultValue (skip creation-time sentinel; materialized at create only)
  if (
    definition.defaultValue !== undefined &&
    !isCreationTimeDefault(definition.defaultValue)
  ) {
    // The defaultValue is already typed (not a string from storage).
    // Normalize it through write then coerce back to get consistent behavior,
    // OR simply return it directly since it's already a typed native value.
    // Per spec: "coerce it from its native type (not as a string)"
    // → return as-is (it's already the native typed value).
    return {
      found: false,
      usedDefault: true,
      typedValue: definition.defaultValue,
      issues: [],
    };
  }

  return {
    found: false,
    usedDefault: false,
    typedValue: undefined,
    issues: [],
  };
}

/**
 * Writes a typed value for a category property to a new arbitraryFieldsList.
 * Normalizes to string. Removes entry if normalized value is null.
 * Returns new array (immutable — does not mutate input).
 * Never throws.
 */
export function writePropertyValue(
  categoryId: string,
  propertyId: string,
  typedValue: unknown,
  fields: readonly ArbitraryFieldEntry[],
  definition: PropertyDefinition,
  policy?: CoercionPolicy,
): CategoryPropertyWriteResult {
  const effectivePolicy = { ...DEFAULT_COERCION_POLICY, ...policy };
  const { storedValue, issues } = normalizeWriteValue(typedValue, definition, effectivePolicy);

  const hasError = issues.some(i => i.severity === 'error');

  if (storedValue === null) {
    // Remove the entry if it exists
    const updatedFields = fields.filter(
      f => !(f.categoryId === categoryId && f.propertyId === propertyId),
    );
    return {
      success: !hasError,
      storedRawValue: undefined,
      removedEntry: true,
      updatedFields,
      issues,
    };
  }

  // Upsert the entry
  const exists = fields.some(
    f => f.categoryId === categoryId && f.propertyId === propertyId,
  );

  let updatedFields: ArbitraryFieldEntry[];
  if (exists) {
    updatedFields = fields.map(f =>
      f.categoryId === categoryId && f.propertyId === propertyId
        ? { categoryId, propertyId, value: storedValue }
        : f,
    );
  } else {
    updatedFields = [...fields, { categoryId, propertyId, value: storedValue }];
  }

  return {
    success: !hasError,
    storedRawValue: storedValue,
    removedEntry: false,
    updatedFields,
    issues,
  };
}

/**
 * Batch-reads multiple properties. Returns a map of propertyId → read result.
 */
export function readPropertiesForCategory(
  categoryId: string,
  propertyIds: string[],
  fields: readonly ArbitraryFieldEntry[],
  definitions: PropertyDefinition[],
  policy?: CoercionPolicy,
): Map<string, CategoryPropertyReadResult> {
  const result = new Map<string, CategoryPropertyReadResult>();
  const defMap = new Map(definitions.map(d => [d.id, d]));

  for (const propertyId of propertyIds) {
    const definition = defMap.get(propertyId);
    if (definition === undefined) {
      result.set(propertyId, {
        found: false,
        usedDefault: false,
        typedValue: undefined,
        issues: [
          {
            severity: 'error',
            code: 'semantic.missing_property',
            message: `No PropertyDefinition found for propertyId "${propertyId}".`,
            path: propertyId,
          },
        ],
      });
      continue;
    }
    result.set(
      propertyId,
      readPropertyValue(categoryId, propertyId, fields, definition, policy),
    );
  }

  return result;
}
